import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { formatApiErrorDetail } from "@/api";
import { Shield, Loader2 } from "lucide-react";

const input = "mt-2 w-full bg-[#121212] border border-white/10 text-sm px-3 py-3 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "goalie", category: "Goalie Coach" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = isRegister ? await register(form) : await login(form.email, form.password);
      nav(u.role === "coach" ? "/coach/dashboard" : (loc.state?.from || "/coaches"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={isRegister ? "register-page" : "login-page"} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <Shield className="w-10 h-10 text-[#00e5ff] mb-6" />
        <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold leading-none">
          {isRegister ? "Create your account" : "Welcome back"}
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed mt-6 max-w-sm">
          {isRegister
            ? "Parents and goalies book sessions. Coaches — on-ice, strength, mobility, vision or mental performance — get a profile, calendar and payouts dashboard."
            : "Log in to manage your bookings, payments and video analysis orders."}
        </p>
      </div>

      <form onSubmit={submit} className="lg:col-span-5 lg:col-start-8 bg-[#121212] border border-white/10 p-8 space-y-5">
        {isRegister && (
          <>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Full name</span>
              <input data-testid="auth-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            </label>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">I am a…</span>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[["goalie", "Goalie / Parent"], ["coach", "Goalie Coach"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`role-${v}-btn`} onClick={() => setForm({ ...form, role: v })}
                    className={`text-[11px] uppercase tracking-wide py-3 border transition-colors duration-200 ${
                      form.role === v ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {isRegister && form.role === "coach" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Discipline</span>
                <select data-testid="auth-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
                  {["Goalie Coach", "Strength & Conditioning", "Mobility Specialist", "Vision & Cognitive", "Mental Performance"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Email</span>
          <input data-testid="auth-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Password</span>
          <input data-testid="auth-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        </label>

        {error && <p data-testid="auth-error" className="text-xs text-[#ff3b30] border border-[#ff3b30]/40 px-3 py-2">{error}</p>}

        <button data-testid="auth-submit-btn" disabled={busy} type="submit"
          className="w-full bg-[#00e5ff] text-black font-bold uppercase tracking-wide py-4 flex items-center justify-center gap-2 hover:bg-[#00b3cc] disabled:opacity-50 transition-colors duration-200">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isRegister ? "Create account" : "Log in"}
        </button>

        <p className="text-xs text-zinc-500">
          {isRegister ? "Already have an account? " : "New here? "}
          <Link data-testid="auth-switch-link" to={isRegister ? "/login" : "/register"} className="text-[#00e5ff] hover:underline">
            {isRegister ? "Log in" : "Create one"}
          </Link>
        </p>
      </form>
    </div>
  );
}
